'use client';

import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { PulseColumnDef } from 'src/components/PulseUI/PulseTable/types';

interface UsePulseTableColumnsOptions {
  columns: PulseColumnDef[];
  minWidth?: number;
  maxWidth?: number;
}

interface UsePulseTableColumnsReturn {
  getColumnWidth: (field: string) => number;
  startResizing: (field: string, e: React.MouseEvent) => void;
  isResizing: boolean;
  resizingField: string | null;
  getGridTemplateColumns: () => string;
}

/**
 * Manages column widths for PulseTable with drag-to-resize support.
 *
 * Provides local state management for column widths with automatic reset
 * when column definitions change. Handles mouse drag interactions with
 * configurable min/max constraints per column.
 *
 * @param options - Configuration object
 * @param options.columns - Column definitions with default widths
 * @param options.minWidth - Global minimum column width (default: 100)
 * @param options.maxWidth - Global maximum column width (default: 500)
 *
 * @returns Column width management API
 *
 * @example
 * ```tsx
 * const columns: PulseColumnDef[] = [
 *   { field: 'name', headerName: 'Name', width: 200, resizable: false },
 *   { field: 'value', headerName: 'Value', width: 150, resizable: true },
 * ];
 *
 * const { getColumnWidth, startResizing, getGridTemplateColumns } =
 *   usePulseTableColumns({ columns });
 *
 * <div style={{ gridTemplateColumns: getGridTemplateColumns() }}>
 *   <PulseHeader
 *     resizable
 *     onStartResize={(e) => startResizing('value', e)}
 *   />
 * </div>
 * ```
 */
const usePulseTableColumns = ({
  columns,
  minWidth = 150,
  maxWidth = 500,
}: UsePulseTableColumnsOptions): UsePulseTableColumnsReturn => {
  // Initialize widths from column definitions
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => {
      const initial: Record<string, number> = {};
      columns.forEach((col) => {
        if (!col.autoWidth) {
          initial[col.field] = col.width ?? 150;
        }
      });
      return initial;
    }
  );

  // Create stable key from columns to detect actual changes
  const columnsKey = useMemo(
    () => columns.map((col) => `${col.field}:${col.width ?? 150}`).join(','),
    [columns]
  );

  // Reset when columns change (handles dynamic columns)
  useEffect(() => {
    const newWidths: Record<string, number> = {};
    columns.forEach((col) => {
      if (!col.autoWidth) {
        newWidths[col.field] = col.width ?? 150;
      }
    });
    setColumnWidths(newWidths);
  }, [columnsKey]);

  // Resize state management
  const [resizingField, setResizingField] = useState<string | null>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Start resizing a column
  const startResizing = useCallback(
    (field: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const column = columns.find((col) => col.field === field);
      if (!column?.resizable) return;

      const currentWidth = columnWidths[field] ?? column.width ?? 150;

      setResizingField(field);
      startXRef.current = e.pageX;
      startWidthRef.current = currentWidth;
    },
    [columns, columnWidths]
  );

  // Track pending width during drag (doesn't trigger re-renders)
  const pendingWidthRef = useRef<number | null>(null);

  // Handle mouse move and mouse up during resize
  useEffect(() => {
    if (!resizingField) return;

    const column = columns.find((col) => col.field === resizingField);
    if (!column) return;

    const colMinWidth = column.minWidth ?? minWidth ?? 50;
    const colMaxWidth = column.maxWidth ?? maxWidth ?? Infinity;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.pageX - startXRef.current;
      const newWidth = Math.max(
        colMinWidth,
        Math.min(colMaxWidth, startWidthRef.current + diff)
      );

      // Store pending width without triggering React re-render
      pendingWidthRef.current = newWidth;

      // Update CSS custom property directly on document for immediate visual feedback
      document.documentElement.style.setProperty(
        `--pulse-col-${resizingField}`,
        `${newWidth}px`
      );
    };

    const handleMouseUp = () => {
      const fieldToClean = resizingField;
      const finalWidth = pendingWidthRef.current;

      if (finalWidth !== null) {
        // Commit the final width to React state
        setColumnWidths((prev) => ({
          ...prev,
          [resizingField]: finalWidth,
        }));
        pendingWidthRef.current = null;
      }

      // Keep resizingField set so getGridTemplateColumns continues using var()
      // The CSS variable holds the correct final value, so there's no jump
      // Clear everything after React has rendered with the new columnWidths
      requestAnimationFrame(() => {
        setResizingField(null);
        document.documentElement.style.removeProperty(
          `--pulse-col-${fieldToClean}`
        );
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingField, columns, minWidth, maxWidth]);

  // Get width for a specific column
  const getColumnWidth = useCallback(
    (field: string) => {
      return columnWidths[field];
    },
    [columnWidths]
  );

  // Generate CSS Grid template string with filler column
  // Uses CSS variables for columns being resized to enable smooth drag updates
  const getGridTemplateColumns = useCallback(() => {
    const columnWidthsString = columns
      .map((col) => {
        if (col.autoWidth && !columnWidths[col.field] && resizingField !== col.field) {
          return 'max-content';
        }
        const width = columnWidths[col.field] ?? col.width ?? 150;
        // Use CSS variable if this column is being resized, otherwise use the state value
        if (resizingField === col.field) {
          return `var(--pulse-col-${col.field}, ${width}px)`;
        }
        return `${width}px`;
      })
      .join(' ');

    // Add a flexible filler column at the end with minimum width to always be visible
    return columnWidthsString
      ? `${columnWidthsString} minmax(150px, 1fr)`
      : 'minmax(150px, 1fr)';
  }, [columns, columnWidths, resizingField]);

  const isResizing = resizingField !== null;

  return {
    getColumnWidth,
    startResizing,
    isResizing,
    resizingField,
    getGridTemplateColumns,
  };
};

export default usePulseTableColumns;
